import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { CopyText, Pill, SvgIcon } from '@/components';
import { AmlTraceHistoryItemData } from '@/redux/models';
import { ChainsDictionary } from '@/config';
import {
  getIconByNetwork,
  getShortHash,
  getFormattedDateWithTime,
} from '@/helpers';
import { AmlStatusEnum } from '@/api/amlbotAPI';
import {
  getStatusModifier,
  getStatusTranslationKey,
} from './TraceHistoryEntry.helpers';

import styles from './TraceHistoryEntry.module.sass';

interface TraceHistoryEntryProps {
  item: AmlTraceHistoryItemData;
  onExpand: () => void;
}

export const TraceHistoryEntry = ({
  item,
  onExpand,
}: TraceHistoryEntryProps) => {
  const { t } = useTranslation();

  const shortHash = getShortHash(item.tx);
  const networkName = ChainsDictionary[item.network]?.name || item.network;
  const isPending = item.status === AmlStatusEnum.PENDING;

  const classes = cx(styles.entry);

  const dateTimeText = useMemo(
    () => (item.timestamp ? getFormattedDateWithTime(item.timestamp) : ''),
    [item.timestamp],
  );

  return (
    <div className={classes}>
      <div className={styles.row1}>
        <div className={styles.iconCol}>
          <img
            src={getIconByNetwork(item.network)}
            alt={item.network}
            className={styles.icon}
          />
        </div>
        <div className={styles.addressCol}>
          <div className={styles.crypto}>
            <div className={styles.crypto}>
              <div className={styles.address}>
                <CopyText text={shortHash} copyText={item.tx || item.address} />
              </div>
              <div className={styles.info}>
                <span className={styles.network}>{networkName}</span>
                <span className={styles.asset}>{item.displayAsset}</span>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.datetimeCol}>
          <span className={styles.datetime}>{dateTimeText}</span>
        </div>
      </div>

      <div className={styles.row2}>
        <div className={styles.statusCol}>
          <span className={styles.mobileLabel}>
            {t('translation.monitoring_table_status')}:
          </span>
          <Pill
            text={t(getStatusTranslationKey(item.status))}
            isRounded
            className={cx(
              styles.statusPill,
              styles[`statusPill--${getStatusModifier(item.status)}`],
            )}
          />
        </div>

        <div className={styles.actionsCol}>
          {!isPending && (
            <button type="button" className={styles.expand} onClick={onExpand}>
              <span className={styles.text}>{t('translation.details')}</span>
              <SvgIcon name="arrow-right-short" className={styles.arrow} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
